// frontend/src/utils/clinicalDictionary.js

/**
 * Extensive Categorized Clinical and Medical Typeahead Dictionary
 * Covers symptoms, vitals/exam findings, diagnoses, and advice/instructions.
 */

// ─── 1. SYMPTOMS & HISTORY OF ILLNESS ───────────────────────────────────────
export const SYMPTOMS_DICTIONARY = [
  // Fever & Temperature
  "fever",
  "fever with chills and rigors",
  "high grade fever since",
  "low grade intermittent fever",
  "fever with body ache",
  "fever with rash",
  "fever with sweating at night",
  "persistent fever for days",
  "evening rise of temperature",
  "history of recurrent fever",
  "chills and rigors",
  "night sweats",

  // Dental & Oral Health
  "cavities",
  "dental cavity pain",
  "toothache",
  "tooth ache severe",
  "tooth decay",
  "tooth sensitivity to cold water",
  "tooth sensitivity to hot food",
  "bleeding gums while brushing",
  "swollen gums",
  "gum pain and redness",
  "gum bleeding",
  "wisdom tooth pain",
  "dental abscess",
  "pus discharge from gums",
  "loose tooth",
  "difficulty chewing food",
  "jaw pain on chewing",
  "mouth ulcers",
  "painful oral sores",
  "bad breath / halitosis",
  "dry mouth",
  "tongue pain and redness",
  "burning sensation in mouth",

  // Head & Neurological
  "headache",
  "severe throbbing headache",
  "migraine with nausea and vomiting",
  "headache since morning",
  "tension headache",
  "headache worse in morning",
  "headache with photophobia",
  "dizziness",
  "giddiness on standing",
  "vertigo with spinning sensation",
  "lightheadedness",
  "fainting episode",
  "blackout",
  "loss of consciousness",
  "confusion and disorientation",
  "memory loss",
  "seizure episode",
  "convulsions",
  "weakness of limbs",
  "numbness and tingling in hands",
  "tingling sensation in feet",
  "tremors in hands",
  "facial numbness",
  "unsteady gait",

  // Respiratory & Lungs
  "lungs pain",
  "lung congestion",
  "lung heaviness",
  "chest congestion",
  "cough",
  "dry cough",
  "productive cough with sputum",
  "cough with yellow/greenish sputum",
  "cough with blood-tinged sputum",
  "coughing up blood (hemoptysis)",
  "cough since days",
  "nocturnal cough",
  "shortness of breath",
  "breathlessness on exertion",
  "breathlessness at rest",
  "difficulty breathing at rest",
  "wheezing sound while breathing",
  "chest tightness",
  "chest pain",
  "sharp chest pain on breathing",
  "pleuritic chest pain",
  "chest heaviness",
  "feeling of suffocation",
  "noisy breathing / stridor",
  "palpitations",
  "rapid heartbeats",
  "shortness of breath while lying flat (orthopnea)",

  // Eyes, Ears, Nose, Throat (ENT & Ophthalmology)
  "sore throat",
  "throat pain and difficulty swallowing",
  "throat congestion",
  "scratchy throat",
  "running nose",
  "blocked nose",
  "nasal congestion with sneezing",
  "sneezing episodes",
  "nasal discharge watery",
  "nasal discharge thick yellow",
  "nosebleed (epistaxis)",
  "loss of smell (anosmia)",
  "loss of taste (ageusia)",
  "voice hoarseness",
  "loss of voice",
  "ear pain (earache)",
  "ear discharge",
  "ear fullness / blocked ear",
  "hearing loss",
  "ringing in ears (tinnitus)",
  "eye redness",
  "eye discharge",
  "pink eye",
  "conjunctivitis symptoms",
  "sticky eye discharge yellow/green",
  "watery eye discharge",
  "eye itching and watering",
  "pain in eyes",
  "foreign body sensation in eye",
  "blurring of vision",
  "double vision (diplopia)",
  "sensitivity to light (photophobia)",
  "swelling of eyelids",
  "stye on eyelid / painful bump",
  "dry eye irritation",
  "burning sensation in eyes",
  "flashing lights in vision",
  "eye floaters",
  "diminished night vision",

  // Gastrointestinal & Food-Related
  "food poisoning",
  "food poisoning with vomiting and diarrhea",
  "food contamination symptoms",
  "food allergy reaction",
  "stomach infection",
  "stomach ache after eating",
  "stomach upset after restaurant food",
  "nausea and vomiting after eating",
  "spicy food intolerance / acidity",
  "lactose intolerance symptoms",
  "seafood allergy / skin hives",
  "indigestion after heavy meal",
  "stomach ache",
  "stomach upset",
  "abdominal pain",
  "epigastric pain",
  "cramping abdominal pain",
  "pain in right upper abdomen",
  "pain in right lower abdomen",
  "nausea",
  "nausea and vomiting",
  "vomiting",
  "vomiting after meals",
  "projectile vomiting",
  "blood in vomit",
  "heartburn",
  "acidity and heartburn",
  "acid reflux",
  "indigestion",
  "bloating and gas",
  "flatulence",
  "diarrhea",
  "loose watery stools",
  "frequent loose motions",
  "blood in stools",
  "black tarry stools",
  "constipation",
  "hard stools",
  "difficulty passing stools",
  "loss of appetite",
  "anorexia",
  "weight loss unintentional",
  "weight gain",
  "jaundice",
  "yellowish discoloration of eyes and skin",
  "abdominal distension",
  "bloating of abdomen",
  "rectal pain and bleeding",
  "piles pain",

  // Urinary & Reproductive
  "burning micturition",
  "pain while urinating",
  "increased urinary frequency",
  "urgency to urinate",
  "blood in urine",
  "cloudy urine",
  "difficulty passing urine",
  "incomplete voiding sensation",
  "lower abdominal pain in females",
  "pelvic pain",
  "vaginal discharge",
  "irregular menstruation",
  "missed periods",
  "heavy menstrual bleeding",
  "painful periods",
  "testicular pain and swelling",
  "flank pain / kidney pain",

  // Musculoskeletal
  "joint pain",
  "joint swelling and stiffness",
  "knee pain bilateral",
  "knee pain right",
  "knee pain left",
  "hip pain",
  "shoulder pain",
  "back pain",
  "lower back pain",
  "upper back pain",
  "neck pain and stiffness",
  "body ache generalized",
  "muscle pain and weakness",
  "leg cramps",
  "foot pain",
  "ankle swelling",
  "swelling in legs",
  "pedal edema bilateral",
  "sciatica pain radiating to leg",
  "heel pain / plantar pain",

  // Skin & Hair
  "skin rash",
  "itching all over body",
  "itching and rash",
  "redness and swelling of skin",
  "blisters on skin",
  "dry scaly patches on skin",
  "hives / urticaria rash",
  "boils on skin",
  "hair fall severe",
  "dandruff and scalp itching",
  "nail changes",
  "wound not healing",
  "dark patches on skin",

  // General & Psychiatric
  "fatigue",
  "generalized weakness",
  "easy fatigability",
  "lethargy",
  "malaise",
  "excessive sweating",
  "weight loss",
  "appetite loss",
  "insomnia",
  "disturbed sleep",
  "anxiety",
  "restlessness",
  "depression",
  "low mood",
  "mood swings",
  "irritability",
  "excessive thirst",
  "increased appetite",
  "polyuria",
  "swelling of face",
  "swelling of hands"
];

// ─── 2. VITALS & PHYSICAL EXAMINATION ───────────────────────────────────────
export const VITALS_EXAM_DICTIONARY = [
  // Vitals
  "BP: 120/80 mmHg",
  "BP: 130/90 mmHg",
  "BP: 140/90 mmHg — Mildly Hypertensive",
  "BP: 100/70 mmHg — Hypotensive",
  "BP: 90/60 mmHg — Hypotensive, IV fluids started",
  "Pulse: 72 bpm, regular, good volume",
  "Pulse: 88 bpm, regular, normal volume",
  "Pulse: 100 bpm, tachycardic, regular",
  "Pulse: 56 bpm, bradycardic",
  "Pulse: Irregularly irregular",
  "Temp: 98.6°F (Afebrile)",
  "Temp: 100.4°F — Low grade fever",
  "Temp: 102°F — Febrile",
  "Temp: 103.5°F — High grade fever",
  "SpO2: 99% on room air",
  "SpO2: 97% on room air",
  "SpO2: 94% on room air — borderline",
  "SpO2: 90% — Supplemental O2 applied",
  "Respiratory Rate: 16/min",
  "Respiratory Rate: 20/min, normal",
  "Respiratory Rate: 24/min, tachypneic",
  "Height: ___ cm, Weight: ___ kg, BMI: ___",

  // General Appearance
  "Patient is conscious, alert, and oriented to time, place, and person",
  "Patient is moderately built and nourished",
  "Patient is well-built and nourished",
  "Patient appears pale, fatigued, and ill-looking",
  "Patient is cooperative and communicating clearly",
  "Patient is in mild distress",
  "Patient is in moderate distress",
  "Patient is in no acute distress",

  // General Examination
  "Afebrile, no pallor, no icterus, no cyanosis, no clubbing, no lymphadenopathy, no pedal edema",
  "Pallor: Present (+)",
  "Pallor: Mild",
  "Pallor: Moderate",
  "Pallor: Absent",
  "Icterus: Present (+)",
  "Icterus: Absent",
  "Cyanosis: Absent",
  "Cyanosis: Peripheral",
  "Clubbing: Absent",
  "Lymphadenopathy: Cervical lymph nodes palpable, non-tender",
  "Lymphadenopathy: Absent",
  "Pedal Edema: Absent",
  "Pedal Edema: Bilateral pitting (+)",
  "Pedal Edema: Bilateral pitting (++)",
  "Dehydration: Mild",
  "Dehydration: Moderate",
  "Dehydration: Absent",
  "Skin: Normal turgor",
  "Skin: Reduced turgor (dehydrated)",

  // Cardiovascular
  "Heart: S1 S2 heard, no murmurs",
  "Heart: S1 S2 normal, S3 heard",
  "Heart: Systolic murmur grade 2/6 heard",
  "Heart sounds: Muffled",
  "JVP: Not raised",
  "JVP: Raised",
  "Apex beat: Normal",

  // Respiratory
  "Chest: Bilateral air entry clear, no added sounds",
  "Chest: Bilateral vesicular breath sounds, clear",
  "Chest: Crepitations at right base",
  "Chest: Crepitations bilateral bases",
  "Chest: Ronchi present bilateral",
  "Chest: Wheeze audible bilateral",
  "Chest: Reduced air entry on right side",
  "Chest: Dull on percussion right base",
  "Chest: Bronchial breath sounds in right upper lobe",
  "Chest: Pleural friction rub present",
  "Chest: Intercostal retractions present, tachypneic",
  "Chest: Use of accessory muscles of respiration",

  // Dental & Oral Examination
  "Oral cavity: Dental cavities / caries visible in upper/lower molars",
  "Oral cavity: Tooth decay present with sensitivity",
  "Oral cavity: Plaque and calculus buildup moderate",
  "Oral cavity: Gingivitis with swollen, red, bleeding gums",
  "Oral cavity: Periodontal pockets present",
  "Oral cavity: Tooth mobility Grade I / II noted",
  "Oral cavity: Impacted 3rd molar with pericoronitis",
  "Oral cavity: Oral mucosal ulceration noted",
  "Oral cavity: White patches on tongue / oral mucosa (Thrush/Candidiasis)",
  "Oral cavity: Good oral hygiene maintained",

  // Abdomen
  "Abdomen: Soft, non-tender, no organomegaly, bowel sounds present and normal",
  "Abdomen: Soft, mild tenderness in epigastric region",
  "Abdomen: Tenderness in right iliac fossa",
  "Abdomen: Rigidity present, guarding noted",
  "Abdomen: Distended, tympanic",
  "Liver: Palpable 2 cm below costal margin, soft, non-tender",
  "Spleen: Not palpable",
  "Bowel sounds: Present and normal",
  "Bowel sounds: Hyperactive",
  "Bowel sounds: Sluggish",
  "Bowel sounds: Absent",

  // Neurological
  "CNS: Conscious, oriented, cranial nerves intact",
  "Motor: Power 5/5 in all four limbs",
  "Motor: Weakness in right upper limb, power 3/5",
  "Sensory: Intact to light touch and pin prick",
  "Reflexes: Deep tendon reflexes 2+ bilaterally",
  "Plantar: Flexor bilaterally",
  "Plantar: Extensor right side (Babinski +)",
  "Gait: Normal",
  "Gait: Unsteady, wide-based",
  "MMSE: ___ / 30",
  "GCS: E4V5M6 = 15",
  "Romberg test: Negative",
  "Cerebellar signs: Negative (no ataxia, no dysmetria)",

  // Musculoskeletal Examination
  "Joints: No active joint swelling or tenderness",
  "Knee joint: Bilateral crepitus on flexion/extension",
  "Knee joint: Effusion present, tender joint line",
  "Spine: Lumbar tenderness over L4-L5 vertebrae",
  "Spine: Paraspinal muscle spasm present",
  "Straight Leg Raise (SLR) test: Positive at 45 degrees right side",
  "Shoulder: Restricted range of motion (Frozen Shoulder)",

  // Eyes/ENT
  "Pupils: Equal and reactive to light (PERRLA)",
  "Pupils: Unequal — right larger than left",
  "Conjunctiva: Pallor present, no congestion",
  "Conjunctiva: Erythematous, watery discharge (Conjunctivitis)",
  "Throat: Congested, erythematous pharynx",
  "Throat: Tonsillar enlargement grade II, exudate present",
  "Throat: Clear",
  "Ears: Tympanic membranes intact bilaterally",
  "Ears: Tympanic membrane red and bulging (Otitis Media)",
  "Ears: Purulent discharge in external auditory canal",
  "Nose: Congested nasal mucosa, no discharge",
  "Nose: Deviated Nasal Septum (DNS) to right/left",
  "Nose: Pale nasal mucosa, watery discharge",

  // Skin & Examination
  "Skin: Warm, dry, intact without lesions",
  "Skin: Maculopapular erythematous rash over trunk and extremities",
  "Skin: Dry, scaly pruritic patches (Eczema / Psoriasis)",
  "Skin: Vesicular lesions on erythematous base (Herpes)",
  "Skin: Fungal ring-shaped scaly lesion (Tinea)"
];

// ─── 3. DIAGNOSIS / ASSESSMENT ───────────────────────────────────────────────
export const DIAGNOSIS_DICTIONARY = [
  // Infections / Fever
  "Acute Upper Respiratory Tract Infection (URTI)",
  "Acute Viral Fever / Pyrexia of Unknown Origin (PUO)",
  "Acute Viral Pharyngitis",
  "Acute Bacterial Tonsillitis",
  "Acute Sinusitis — Bacterial",
  "Acute Otitis Media",
  "Influenza / Flu-like illness",
  "Dengue Fever — Suspected",
  "Typhoid Fever — Suspected",
  "Malaria — Suspected (P. falciparum)",
  "Chikungunya — Suspected",
  "COVID-19 — Suspected / Confirmed",
  "Tuberculosis — Pulmonary, suspected",
  "Urinary Tract Infection (UTI) — Uncomplicated",
  "Urinary Tract Infection (UTI) — Complicated",
  "Cellulitis of right leg",
  "Wound infection — local",
  "Fungal Infection — Tinea Corporis",
  "Scabies",
  "Herpes Zoster",
  "Viral Conjunctivitis",

  // Respiratory
  "Acute Bronchitis",
  "Community Acquired Pneumonia (CAP)",
  "Hospital Acquired Pneumonia (HAP)",
  "Bronchial Asthma — Acute exacerbation",
  "Bronchial Asthma — Stable",
  "Chronic Obstructive Pulmonary Disease (COPD) — Exacerbation",
  "Allergic Rhinitis",
  "Pleural Effusion — right side",

  // Gastrointestinal & Food-Related Conditions
  "Food Poisoning — Bacterial (Staphylococcal / Salmonella)",
  "Food Poisoning — Suspected",
  "Food Poisoning — Acute Gastroenteritis",
  "Food Allergy — Acute Allergic Reaction",
  "Food Intolerance — Lactose Intolerance",
  "Food Intolerance — Celiac Disease / Gluten Sensitivity",
  "Amoebic Dysentery / Amoebiasis",
  "Bacillary Dysentery / Shigellosis",
  "Salmonella Gastroenteritis",
  "Traveler's Diarrhea",
  "Acute Gastroenteritis with mild dehydration",
  "Acute Gastroenteritis with moderate dehydration",
  "Acute Gastritis",
  "Gastroesophageal Reflux Disease (GERD)",
  "Peptic Ulcer Disease (PUD)",
  "Irritable Bowel Syndrome (IBS)",
  "Constipation — Functional",
  "Acute Appendicitis — Suspected",
  "Acute Cholecystitis",
  "Cholelithiasis / Gallstones",
  "Acute Pancreatitis",
  "Chronic Hepatitis B",
  "Chronic Hepatitis C",
  "Liver Cirrhosis — Compensated",
  "Liver Cirrhosis — Decompensated",
  "Alcohol-related Liver Disease",

  // Ophthalmology & Eye Diseases
  "Viral Conjunctivitis (Pink Eye)",
  "Bacterial Conjunctivitis",
  "Allergic Conjunctivitis",
  "Vernal Keratoconjunctivitis (VKC)",
  "Stye / Acute Hordeolum",
  "Chalazion",
  "Blepharitis — Anterior / Posterior",
  "Dry Eye Syndrome / Keratoconjunctivitis Sicca",
  "Corneal Ulcer / Bacterial Keratitis",
  "Subconjunctival Hemorrhage",
  "Senile Cataract — Immature / Mature",
  "Primary Open Angle Glaucoma (POAG)",
  "Acute Angle Closure Glaucoma",
  "Pterygium",
  "Diabetic Retinopathy — Non-Proliferative / Proliferative",
  "Hypertensive Retinopathy — Grade I-IV",
  "Refractive Error — Myopia",
  "Refractive Error — Hypermetropia",
  "Refractive Error — Astigmatism",
  "Presbyopia",
  "Uveitis — Anterior (Iritis)",
  "Episcleritis",

  // Cardiovascular
  "Essential Hypertension — Controlled",
  "Essential Hypertension — Uncontrolled",
  "Hypertensive Urgency",
  "Hypertensive Emergency",
  "Coronary Artery Disease (CAD)",
  "Stable Angina",
  "Unstable Angina",
  "Acute Myocardial Infarction (STEMI)",
  "Acute Myocardial Infarction (NSTEMI)",
  "Congestive Heart Failure (CHF)",
  "Atrial Fibrillation",
  "Supraventricular Tachycardia (SVT)",
  "Peripheral Vascular Disease (PVD)",
  "Deep Vein Thrombosis (DVT)",

  // Endocrine / Metabolic
  "Type 2 Diabetes Mellitus (T2DM) — Controlled",
  "Type 2 Diabetes Mellitus (T2DM) — Uncontrolled",
  "Type 1 Diabetes Mellitus (T1DM)",
  "Diabetic Ketoacidosis (DKA)",
  "Hypoglycemia",
  "Dyslipidemia / Hypercholesterolemia",
  "Hypothyroidism",
  "Hyperthyroidism / Graves Disease",
  "Obesity — BMI > 30",
  "Iron Deficiency Anemia",
  "Vitamin B12 Deficiency",
  "Vitamin D Deficiency",

  // Neurology
  "Migraine — Without Aura",
  "Migraine — With Aura",
  "Tension-type Headache",
  "Cluster Headache",
  "Ischemic Stroke (CVA)",
  "Transient Ischemic Attack (TIA)",
  "Epilepsy — Focal seizures",
  "Epilepsy — Generalized seizures",
  "Peripheral Neuropathy",
  "Lumbar Radiculopathy / Sciatica",
  "Carpal Tunnel Syndrome",
  "Parkinson's Disease",
  "Alzheimer's Dementia",

  // Musculoskeletal
  "Osteoarthritis of knees — Bilateral",
  "Osteoarthritis of hips",
  "Rheumatoid Arthritis",
  "Gouty Arthritis — Acute",
  "Lumbar Spondylosis",
  "Cervical Spondylosis",
  "Mechanical Low Back Pain",
  "Rotator Cuff Tear — right shoulder",
  "Osteoporosis",
  "Fracture of right wrist — Colles",
  "Sprain — right ankle",

  // Dermatology
  "Atopic Dermatitis / Eczema",
  "Contact Dermatitis",
  "Psoriasis",
  "Acne Vulgaris",
  "Urticaria — Acute",
  "Urticaria — Chronic",
  "Seborrheic Dermatitis",

  // Mental Health
  "Generalized Anxiety Disorder (GAD)",
  "Major Depressive Disorder (MDD)",
  "Panic Disorder",
  "Adjustment Disorder with Anxiety",
  "Insomnia Disorder — Primary",
  "Attention Deficit Hyperactivity Disorder (ADHD)",
  "Bipolar Affective Disorder (BPAD)",
  "Schizophrenia",
  "Obsessive Compulsive Disorder (OCD)",
  "Post-Traumatic Stress Disorder (PTSD)",

  // Renal
  "Chronic Kidney Disease (CKD) Stage ___",
  "Acute Kidney Injury (AKI)",
  "Urolithiasis / Renal Calculus",
  "Nephrotic Syndrome",
  "Benign Prostatic Hyperplasia (BPH)",

  // Gynecology / Obstetrics
  "Polycystic Ovary Syndrome (PCOS)",
  "Dysmenorrhea — Primary",
  "Dysmenorrhea — Secondary",
  "Pelvic Inflammatory Disease (PID)",
  "Pregnancy — ___ weeks gestation",
  "Gestational Diabetes Mellitus (GDM)",
  "Preeclampsia",
  "Eclampsia",
  "Threatened Abortion",
  "Incomplete Abortion",
  "Ectopic Pregnancy — Suspected",
  "Fibroid Uterus",
  "Endometriosis",
  "Cervical Erosion",
  "Menorrhagia",
  "Metrorrhagia",
  "Amenorrhea — Primary",
  "Amenorrhea — Secondary",
  "Ovarian Cyst — Right / Left",
  "Vaginitis — Candidal",
  "Vaginitis — Bacterial",
  "Postpartum Depression",

  // ─── DENTAL / ORAL HEALTH ─────────────────────────────────────────────────
  "Dental Caries — Multiple teeth",
  "Dental Caries — Upper molar right",
  "Dental Abscess — Periapical",
  "Periodontal Disease / Periodontitis",
  "Gingivitis — Acute",
  "Gingivitis — Chronic",
  "Stomatitis — Aphthous (Mouth Ulcers)",
  "Oral Candidiasis (Oral Thrush)",
  "Tooth Sensitivity — Dentinal Hypersensitivity",
  "Tooth Extraction — Indicated",
  "Impacted Wisdom Tooth — Lower Right",
  "Impacted Wisdom Tooth — Lower Left",
  "Temporomandibular Joint Disorder (TMJ/TMD)",
  "Bruxism / Teeth Grinding",
  "Malocclusion",
  "Dental Fluorosis",
  "Dry Socket — Post Extraction",
  "Root Canal Treatment — Indicated",
  "Cracked Tooth Syndrome",
  "Leukoplakia — Oral",
  "Lichen Planus — Oral",
  "Halitosis / Bad Breath",
  "Denture Stomatitis",
  "Cleft Lip and Palate",
  "Tongue Tie (Ankyloglossia)",

  // ─── ENT (Ear, Nose, Throat) ──────────────────────────────────────────────
  "Acute Otitis Media",
  "Chronic Suppurative Otitis Media (CSOM) — Tubotympanic type",
  "Chronic Suppurative Otitis Media (CSOM) — Atticoantral type",
  "Otitis Externa — Diffuse",
  "Otitis Externa — Furunculosis",
  "Sensorineural Hearing Loss — Bilateral",
  "Conductive Hearing Loss — Right",
  "Presbycusis — Age-related Hearing Loss",
  "Tinnitus — Subjective",
  "Benign Paroxysmal Positional Vertigo (BPPV)",
  "Meniere's Disease",
  "Earwax Impaction — Bilateral",
  "Perforated Tympanic Membrane — Right",
  "Eustachian Tube Dysfunction",
  "Acute Sinusitis — Maxillary",
  "Acute Sinusitis — Frontal",
  "Chronic Sinusitis",
  "Deviated Nasal Septum (DNS)",
  "Nasal Polyposis",
  "Epistaxis — Anterior",
  "Epistaxis — Posterior",
  "Adenoid Hypertrophy",
  "Acute Tonsillitis — Follicular",
  "Chronic Tonsillitis",
  "Peritonsillar Abscess",
  "Retropharyngeal Abscess",
  "Acute Epiglottitis",
  "Laryngitis — Acute",
  "Vocal Cord Nodules",
  "Vocal Cord Polyp",
  "Hoarseness — Dysphonia",
  "Snoring / Obstructive Sleep Apnea (OSA)",
  "Foreign Body in Ear",
  "Foreign Body in Nose",
  "Foreign Body in Throat",
  "Parotitis — Viral (Mumps)",
  "Parotid Calculus / Sialolithiasis",

  // ─── OPHTHALMOLOGY / EYE ─────────────────────────────────────────────────
  "Conjunctivitis — Allergic",
  "Conjunctivitis — Bacterial",
  "Conjunctivitis — Viral",
  "Dry Eye Syndrome",
  "Blepharitis — Anterior",
  "Blepharitis — Posterior / Meibomian",
  "Hordeolum / Stye",
  "Chalazion",
  "Dacryocystitis — Acute",
  "Corneal Ulcer",
  "Keratitis — Bacterial",
  "Keratitis — Viral (Herpes)",
  "Uveitis — Anterior",
  "Glaucoma — Primary Open Angle",
  "Glaucoma — Acute Angle Closure",
  "Cataract — Nuclear Sclerosis Grade II",
  "Age-Related Macular Degeneration (ARMD)",
  "Diabetic Retinopathy — Non-Proliferative",
  "Diabetic Retinopathy — Proliferative",
  "Hypertensive Retinopathy",
  "Retinal Detachment — Suspected",
  "Refractive Error — Myopia",
  "Refractive Error — Hypermetropia",
  "Refractive Error — Astigmatism",
  "Presbyopia",
  "Amblyopia / Lazy Eye",
  "Strabismus — Convergent",
  "Strabismus — Divergent",
  "Subconjunctival Hemorrhage",
  "Pterygium",
  "Entropion / Ectropion",
  "Optic Neuritis",
  "Cellulitis — Periorbital",
  "Cellulitis — Orbital",

  // ─── DERMATOLOGY / SKIN (Extended) ───────────────────────────────────────
  "Atopic Dermatitis / Eczema — Chronic",
  "Contact Dermatitis — Allergic",
  "Contact Dermatitis — Irritant",
  "Psoriasis — Plaque Type",
  "Psoriasis — Guttate",
  "Psoriatic Arthritis",
  "Acne Vulgaris — Mild",
  "Acne Vulgaris — Moderate",
  "Acne Vulgaris — Severe (Nodulocystic)",
  "Rosacea",
  "Urticaria — Acute",
  "Urticaria — Chronic Spontaneous",
  "Angioedema",
  "Seborrheic Dermatitis",
  "Dandruff (Pityriasis Capitis)",
  "Sebaceous Cyst",
  "Lipoma",
  "Keloid",
  "Hypertrophic Scar",
  "Vitiligo",
  "Melasma / Chloasma",
  "Tinea Corporis (Ringworm)",
  "Tinea Cruris (Jock Itch)",
  "Tinea Pedis (Athlete's Foot)",
  "Tinea Capitis",
  "Tinea Versicolor (Pityriasis Versicolor)",
  "Onychomycosis — Fingernail / Toenail",
  "Candidiasis — Intertriginous",
  "Herpes Zoster (Shingles)",
  "Herpes Simplex — Labialis (Cold Sore)",
  "Herpes Simplex — Genitalis",
  "Warts — Common (Verruca Vulgaris)",
  "Warts — Plantar (Verruca Plantaris)",
  "Molluscum Contagiosum",
  "Impetigo — Bullous",
  "Impetigo — Non-Bullous",
  "Cellulitis",
  "Erysipelas",
  "Abscess — Skin",
  "Folliculitis",
  "Furunculosis / Boil",
  "Carbuncle",
  "Scabies",
  "Pediculosis — Capitis (Head Lice)",
  "Pediculosis — Pubis (Pubic Lice)",
  "Alopecia Areata",
  "Androgenetic Alopecia — Male Pattern Baldness",
  "Telogen Effluvium",
  "Lichen Planus — Cutaneous",
  "Pityriasis Rosea",
  "Bullous Pemphigoid",
  "Pemphigus Vulgaris",
  "Stevens-Johnson Syndrome (SJS)",
  "Drug Rash / Drug-Induced Dermatitis",
  "Photodermatitis / Sun Allergy",
  "Pruritus — Generalized (no primary lesion)",
  "Basal Cell Carcinoma (BCC)",
  "Squamous Cell Carcinoma (SCC) — Skin",
  "Melanoma — Suspected",
  "Seborrheic Keratosis",
  "Actinic Keratosis",

  // ─── PEDIATRICS ───────────────────────────────────────────────────────────
  "Neonatal Jaundice — Physiological",
  "Neonatal Jaundice — Pathological",
  "Neonatal Sepsis",
  "Birth Asphyxia",
  "Developmental Delay — Global",
  "Failure to Thrive",
  "Protein Energy Malnutrition (PEM) — Grade I / II",
  "Rickets — Nutritional",
  "Infant Colic",
  "Gastroesophageal Reflux — Infant",
  "Intussusception",
  "Pyloric Stenosis",
  "Febrile Seizures",
  "Acute Bronchiolitis",
  "Croup — Acute Laryngotracheobronchitis",
  "Whooping Cough (Pertussis)",
  "Measles (Rubeola)",
  "Mumps",
  "Chickenpox (Varicella)",
  "Hand, Foot and Mouth Disease",
  "Kawasaki Disease",
  "Henoch-Schönlein Purpura (HSP)",
  "Nephrotic Syndrome — Childhood",
  "Juvenile Idiopathic Arthritis (JIA)",
  "Autism Spectrum Disorder (ASD)",
  "Attention Deficit Hyperactivity Disorder (ADHD) — Pediatric",
  "Cerebral Palsy — Spastic",
  "Down Syndrome",
  "Congenital Heart Disease — VSD",
  "Congenital Heart Disease — ASD",
  "Congenital Heart Disease — PDA",
  "Congenital Hypothyroidism",
  "Thalassemia Major",
  "Sickle Cell Disease",
  "Hemophilia A / B",

  // ─── ORTHOPEDICS (Extended) ───────────────────────────────────────────────
  "Fracture Neck of Femur",
  "Fracture Intertrochanteric",
  "Fracture Shaft of Femur",
  "Fracture Tibia — Compound",
  "Fracture Clavicle",
  "Fracture Distal Radius — Colles",
  "Fracture Radius Ulna — Both Bones",
  "Fracture Humerus — Proximal",
  "Fracture Surgical Neck of Humerus",
  "Fracture Pelvis",
  "Vertebral Compression Fracture — L1",
  "Ligament Tear — ACL",
  "Ligament Tear — PCL",
  "Medial Meniscus Tear",
  "Lateral Meniscus Tear",
  "Shoulder Impingement Syndrome",
  "Frozen Shoulder (Adhesive Capsulitis)",
  "Tennis Elbow (Lateral Epicondylitis)",
  "Golfer's Elbow (Medial Epicondylitis)",
  "Trigger Finger",
  "De Quervain's Tenosynovitis",
  "Ganglion Cyst — Wrist",
  "Hallux Valgus (Bunion)",
  "Plantar Fasciitis",
  "Achilles Tendinopathy",
  "Osgood-Schlatter Disease",
  "Spondylolisthesis — L4-L5",
  "Spinal Stenosis — Lumbar",
  "Disc Prolapse — L4-L5",
  "Disc Prolapse — L5-S1",
  "Disc Prolapse — C5-C6",
  "Avascular Necrosis of Femoral Head",
  "Knee Replacement — Total (TKR) — Post-op",
  "Hip Replacement — Total (THR) — Post-op",

  // ─── UROLOGY ─────────────────────────────────────────────────────────────
  "Urinary Tract Infection (UTI) — Lower (Cystitis)",
  "Urinary Tract Infection (UTI) — Upper (Pyelonephritis)",
  "Urethral Stricture",
  "Vesicoureteral Reflux",
  "Neurogenic Bladder",
  "Overactive Bladder (OAB)",
  "Stress Urinary Incontinence",
  "Urge Incontinence",
  "Renal Calculus — Right side",
  "Renal Calculus — Left side",
  "Ureteric Calculus — Upper ureter",
  "Hydronephrosis — Right",
  "Varicocele — Left",
  "Hydrocele",
  "Testicular Torsion — Suspected",
  "Orchitis — Viral",
  "Epididymo-Orchitis",
  "Phimosis",
  "Prostatitis — Acute Bacterial",
  "Prostatitis — Chronic",
  "Benign Prostatic Hyperplasia (BPH) — Grade II/III",
  "Carcinoma Prostate — Suspected",
  "Carcinoma Bladder — Transitional Cell",
  "Renal Cell Carcinoma — Suspected",
  "Erectile Dysfunction",
  "Premature Ejaculation",

  // ─── HEMATOLOGY / ONCOLOGY ────────────────────────────────────────────────
  "Iron Deficiency Anemia",
  "Megaloblastic Anemia — B12 Deficiency",
  "Megaloblastic Anemia — Folate Deficiency",
  "Aplastic Anemia",
  "Hemolytic Anemia",
  "Thalassemia Trait (Minor)",
  "Polycythemia Vera",
  "Essential Thrombocythemia",
  "Idiopathic Thrombocytopenic Purpura (ITP)",
  "Disseminated Intravascular Coagulation (DIC)",
  "Deep Vein Thrombosis (DVT) — Left leg",
  "Pulmonary Embolism (PE)",
  "Acute Myeloid Leukemia (AML)",
  "Acute Lymphoblastic Leukemia (ALL)",
  "Chronic Myeloid Leukemia (CML)",
  "Chronic Lymphocytic Leukemia (CLL)",
  "Non-Hodgkin's Lymphoma",
  "Hodgkin's Lymphoma",
  "Multiple Myeloma",
  "Carcinoma Breast — Right / Left",
  "Carcinoma Cervix — Stage ___",
  "Carcinoma Endometrium",
  "Carcinoma Ovary",
  "Carcinoma Colon",
  "Carcinoma Rectum",
  "Hepatocellular Carcinoma (HCC)",
  "Carcinoma Lung — Non-Small Cell (NSCLC)",
  "Carcinoma Lung — Small Cell (SCLC)",
  "Carcinoma Stomach / Gastric Carcinoma",
  "Carcinoma Esophagus",
  "Carcinoma Pancreas",
  "Carcinoma Thyroid — Papillary",
  "Carcinoma Thyroid — Follicular",

  // ─── INFECTIOUS DISEASES (Extended) ──────────────────────────────────────
  "Dengue Fever — NS1 Positive",
  "Dengue Hemorrhagic Fever",
  "Malaria — P. falciparum",
  "Malaria — P. vivax",
  "Typhoid Fever (Enteric Fever)",
  "Leptospirosis",
  "Brucellosis",
  "Scrub Typhus",
  "Rickettsial Fever",
  "COVID-19 — Mild",
  "COVID-19 — Moderate",
  "COVID-19 — Severe",
  "Post-COVID Syndrome (Long COVID)",
  "Tuberculosis — Pulmonary",
  "Tuberculosis — Extrapulmonary",
  "Tuberculosis — Lymphadenitis",
  "Tuberculosis — Pleural",
  "Tuberculosis — Meningitis",
  "HIV/AIDS — CD4 count ___",
  "HIV with Opportunistic Infection",
  "Viral Hepatitis A — Acute",
  "Viral Hepatitis B — Acute",
  "Viral Hepatitis C — Chronic",
  "Viral Hepatitis E — Acute",
  "Infectious Mononucleosis (EBV)",
  "Cytomegalovirus (CMV) Infection",
  "Chikungunya",
  "Zika Virus Infection",
  "Japanese Encephalitis",
  "Rabies — Post-exposure Prophylaxis",
  "Tetanus",
  "Meningococcal Meningitis",
  "Bacterial Meningitis",
  "Viral Meningitis",
  "Septicemia / Sepsis",
  "Septic Shock",
  "Cellulitis — Necrotizing Fasciitis",
  "Gas Gangrene",
  "Osteomyelitis — Acute",
  "Osteomyelitis — Chronic",

  // ─── GASTROENTEROLOGY (Extended) ─────────────────────────────────────────
  "Gastroesophageal Reflux Disease (GERD) — Erosive",
  "Gastroesophageal Reflux Disease (GERD) — Non-erosive",
  "Barrett's Esophagus",
  "Esophageal Varices — Grade II",
  "Achalasia Cardia",
  "Hiatus Hernia",
  "Duodenal Ulcer",
  "Gastric Ulcer",
  "H. pylori Infection",
  "Crohn's Disease",
  "Ulcerative Colitis — Mild",
  "Ulcerative Colitis — Moderate",
  "Ulcerative Colitis — Severe",
  "Colorectal Polyps",
  "Hemorrhoids — Grade I / II / III",
  "Anal Fissure",
  "Perianal Abscess",
  "Fistula in Ano",
  "Rectal Prolapse",
  "Diverticulitis",
  "Diverticulosis",
  "Acute Mesenteric Ischemia",
  "Inguinal Hernia — Direct / Indirect",
  "Umbilical Hernia",
  "Incisional Hernia",
  "Portal Hypertension",
  "Spontaneous Bacterial Peritonitis (SBP)",
  "Alcoholic Fatty Liver Disease",
  "Non-Alcoholic Fatty Liver Disease (NAFLD)",
  "Non-Alcoholic Steatohepatitis (NASH)",
  "Primary Biliary Cholangitis",
  "Autoimmune Hepatitis",
  "Wilson's Disease",
  "Hemochromatosis",
  "Primary Sclerosing Cholangitis",
];


// ─── 4. ADVICE, INSTRUCTIONS & FOLLOW-UP ────────────────────────────────────
export const ADVICE_DICTIONARY = [
  // Hydration & Rest
  "Plenty of oral fluids (water, ORS, warm soup, coconut water) and adequate bed rest",
  "Strict bed rest for ___ days",
  "Increase fluid intake to at least 3 liters per day",
  "Oral Rehydration Solution (ORS) — 1 sachet in 1L water, sip frequently",

  // Diet
  "Eat small frequent meals, avoid skipping meals",
  "Avoid spicy, oily, deep-fried, and junk food",
  "Avoid citrus fruits and acidic foods",
  "Low-salt diet (less than 2g sodium per day)",
  "Low-fat diet, avoid red meat and saturated fats",
  "Diabetic diet — avoid sugar, sweets, refined carbohydrates",
  "High-fiber diet including fruits, vegetables, and whole grains",
  "Soft bland diet for 3-5 days",
  "Avoid alcohol completely",
  "Avoid smoking and tobacco products",

  // Medications
  "Take all medications as prescribed; do not stop without consulting",
  "Complete the full course of antibiotics even if feeling better",
  "Avoid self-medication and over-the-counter painkillers without advice",
  "Take antacid before meals and H2 blocker / PPI at bedtime",
  "Inhaler technique: shake, exhale fully, inhale slowly while pressing, hold 10 sec",
  "Measure blood pressure every morning and evening; maintain a log",
  "Check fasting blood sugar every morning before breakfast",
  "Check random blood sugar 2 hours after meals (Postprandial)",

  // Physical Activity
  "Regular brisk walk for 30-45 minutes, 5 days a week",
  "Avoid strenuous physical activity for ___ weeks",
  "Physiotherapy exercises as advised — do regularly",
  "Swimming or cycling preferred to running for joint protection",
  "Core strengthening exercises for lower back",
  "Avoid heavy lifting and bending forward",

  // ENT / Respiratory
  "Warm saline gargles 3-4 times daily",
  "Steam inhalation with eucalyptus oil twice daily",
  "Use nasal saline spray twice daily",
  "Use prescribed nasal steroid spray regularly",
  "Sleep with head elevated 30 degrees",
  "Avoid cold beverages, ice cream, and cold air exposure",
  "Incentive spirometry exercises 10 times every hour while awake",
  "Perform deep breathing and coughing exercises regularly",
  "Avoid exposure to smoke, dust, and outdoor pollution",
  "Rinse mouth thoroughly with water after using steroid inhaler",

  // Dental & Oral Care
  "Brush teeth twice daily with fluoridated toothpaste for 2 minutes",
  "Floss daily between teeth to clean interdental spaces",
  "Rinse mouth with warm saline water after meals",
  "Avoid sweet, sugary, sticky foods and carbonated drinks",
  "Consult dentist for dental filling / root canal treatment (RCT) evaluation",
  "Use soft-bristled toothbrush and replace every 3 months",
  "Avoid using teeth to bite hard objects",

  // Musculoskeletal & Joint Care
  "Apply ice pack to affected joint for 15 minutes, 3 times daily",
  "Apply hot water bag / heating pad for 20 minutes for muscle spasm relief",
  "Maintain ergonomic posture while sitting and working on computer",
  "Use lumbar support cushion while driving or sitting for long hours",
  "Wear supportive knee brace / lumbar belt as advised",

  // Eye & ENT Care
  "Do not insert cotton buds or sharp objects into ear canal",
  "Keep ears dry while bathing; avoid swimming until clear",
  "Wear protective sunglasses outdoors to prevent eye irritation",
  "Apply warm compress to eyelids for 5-10 minutes twice daily",

  // Skin
  "Apply moisturizer twice daily on affected areas",
  "Avoid scratching; cut nails short",
  "Use fragrance-free soap and detergent",
  "Wear loose, cotton clothing",
  "Avoid known allergens and irritants",

  // Mental Health / Sleep
  "Maintain regular sleep schedule (sleep and wake same time daily)",
  "Practice relaxation techniques — deep breathing, meditation",
  "Limit screen time 1 hour before bedtime",
  "Engage in regular physical activity for mood improvement",
  "Consider psychological counselling / CBT as adjunct to medication",

  // Follow-Up & Emergency
  "Review in OPD after 3 days with reports",
  "Review after 5 days or earlier if no improvement",
  "Review after 2 weeks with investigation reports",
  "Review in 1 month for medication adjustment",
  "Report immediately if: high fever > 103°F, severe chest pain, difficulty breathing, unable to walk, persistent vomiting with no oral intake",
  "Emergency red flag signs — visit ER: sudden severe headache, slurred speech, facial drooping, arm weakness",
  "Investigations advised: CBC, ESR, CRP, Urine R/E — collect and bring on next visit",
  "Investigations advised: HbA1c, Fasting Lipid Profile, LFT, KFT",
  "X-ray Chest PA view — advised",
  "USG Abdomen — advised for further evaluation"
];

// ─── 5. GENERAL CLINICAL WORDS (Fallback) ────────────────────────────────────
export const GENERAL_CLINICAL_WORDS = [
  "patient", "complains", "history", "presenting", "examination", "symptoms", "duration",
  "since", "severity", "progressive", "intermittent", "persistent", "relieved", "aggravated",
  "associated", "medication", "prescription", "investigation", "treatment", "follow-up",
  "continue", "continues", "continued", "ongoing", "running", "upset", "tablet", "capsule",
  "syrup", "injection", "daily", "twice", "thrice", "morning", "night", "before", "after",
  "meals", "routine", "urgent", "bilateral", "unilateral", "right", "left", "moderate",
  "severe", "mild", "acute", "chronic", "subacute", "gradually", "suddenly", "worsening",
  "improving", "stable", "unstable", "controlled", "uncontrolled", "significant", "no",
  "negative", "positive", "normal", "abnormal", "clinical", "surgical", "family", "personal",
  "social", "allergic", "denies", "reports", "complaining", "referred", "provisional",
  "differential", "diagnosis", "assessment", "plan", "advice", "instructed", "informed",
  "cavity", "cavities", "tooth", "teeth", "dental", "gum", "gums", "molar", "incisor", "canine",
  "lung", "lungs", "breath", "breathing", "bronchial", "pulmonary", "respiratory", "cardiac",
  "vascular", "neurological", "dermatological", "ophthalmic", "orthopedic", "urological", "gynecological",
  "pediatric", "psychiatric", "gastrointestinal", "endocrinological", "nephrological", "hematological",
  "oncological", "otolaryngology", "radiology", "pathology", "microbiology",
  "food", "poisoning", "gastroenteritis", "conjunctivitis", "stye", "cataract", "glaucoma", "retinopathy",
  "allergy", "intolerance", "salmonella", "dysentery", "stomatitis", "myopia", "astigmatism"
];

/**
 * Returns matching suggestions for the given prefix within a category.
 * Prioritizes prefix matches over substring matches.
 */
export function getTypeaheadSuggestions(prefix, category = "symptoms", limit = 6) {
  if (!prefix || prefix.trim().length < 2) return [];
  const query = prefix.toLowerCase().trim();

  let primaryDict = [];
  switch (category) {
    case "symptoms":
      primaryDict = SYMPTOMS_DICTIONARY;
      break;
    case "vitals":
      primaryDict = VITALS_EXAM_DICTIONARY;
      break;
    case "diagnosis":
      primaryDict = DIAGNOSIS_DICTIONARY;
      break;
    case "advice":
      primaryDict = ADVICE_DICTIONARY;
      break;
    default:
      primaryDict = SYMPTOMS_DICTIONARY;
  }

  const prefixMatches = [];
  const substringMatches = [];

  for (const item of primaryDict) {
    const itemLower = item.toLowerCase();
    if (itemLower.startsWith(query)) {
      prefixMatches.push(item);
    } else if (itemLower.includes(query)) {
      substringMatches.push(item);
    }
  }

  // Fallback from general clinical words
  const generalMatches = [];
  for (const word of GENERAL_CLINICAL_WORDS) {
    const wl = word.toLowerCase();
    if (wl.startsWith(query) && !prefixMatches.includes(word) && !substringMatches.includes(word)) {
      generalMatches.push(word);
    }
  }

  const combined = [...prefixMatches, ...substringMatches, ...generalMatches];
  const unique = Array.from(new Set(combined));
  return unique.slice(0, limit);
}
